import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { SourceCombobox } from "@/components/review/SourceCombobox";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  useIngestExternalReview,
  useIngestExternalReviewsBulk,
} from "@/hooks/useExternalReviews";
import { ApiError } from "@/services/api";
import type {
  ExternalReviewCreate,
  ExternalReviewPlatform,
} from "@/types/externalReview";
import {
  EXTERNAL_REVIEW_PLATFORMS,
  PLATFORM_LABELS,
} from "@/types/externalReview";

interface SingleFormValues {
  platform: ExternalReviewPlatform;
  url: string;
  text: string;
  author_handle: string;
  external_id: string;
  native_rating: string;
  native_rating_scale: string;
  posted_at: string;
  is_vendor_engagement: boolean;
}

interface BulkRow {
  key: string;
  platform: ExternalReviewPlatform;
  url: string;
  text: string;
  author_handle: string;
}

interface IngestReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sources: string[];
  sourcesLoading?: boolean;
}

const defaultSingle: SingleFormValues = {
  platform: "other",
  url: "",
  text: "",
  author_handle: "",
  external_id: "",
  native_rating: "",
  native_rating_scale: "",
  posted_at: "",
  is_vendor_engagement: false,
};

function emptyBulkRow(): BulkRow {
  return {
    key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    platform: "other",
    url: "",
    text: "",
    author_handle: "",
  };
}

function optionalString(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function optionalNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function toCreatePayload(values: SingleFormValues): ExternalReviewCreate {
  const posted = optionalString(values.posted_at);
  return {
    platform: values.platform,
    url: values.url.trim(),
    text: optionalString(values.text),
    author_handle: optionalString(values.author_handle),
    external_id: optionalString(values.external_id),
    native_rating: optionalNumber(values.native_rating),
    native_rating_scale: optionalNumber(values.native_rating_scale),
    posted_at: posted ? new Date(posted).toISOString() : null,
    is_vendor_engagement: values.is_vendor_engagement,
  };
}

function handleApiError(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    toast.error(error.message);
    return;
  }
  toast.error(error instanceof Error ? error.message : fallback);
}

export function IngestReviewDialog({
  open,
  onOpenChange,
  sources,
  sourcesLoading,
}: IngestReviewDialogProps) {
  const ingestOne = useIngestExternalReview();
  const ingestBulk = useIngestExternalReviewsBulk();
  const [sourceId, setSourceId] = useState<string | undefined>();
  const [tab, setTab] = useState("single");
  const [bulkRows, setBulkRows] = useState<BulkRow[]>([emptyBulkRow()]);
  const [jsonText, setJsonText] = useState("[]");

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SingleFormValues>({
    defaultValues: defaultSingle,
  });

  const platform = watch("platform");
  const isVendorEngagement = watch("is_vendor_engagement");

  useEffect(() => {
    if (open) {
      setSourceId(undefined);
      setTab("single");
      reset(defaultSingle);
      setBulkRows([emptyBulkRow()]);
      setJsonText("[]");
    }
  }, [open, reset]);

  const pending = ingestOne.isPending || ingestBulk.isPending;

  const sourceMissing = !sourceId;

  const onSingleSubmit = handleSubmit(async (values) => {
    if (!sourceId) {
      toast.error("Select a source");
      return;
    }
    if (!values.url.trim()) {
      toast.error("URL is required");
      return;
    }
    try {
      const created = await ingestOne.mutateAsync({
        sourceId,
        body: toCreatePayload(values),
      });
      toast.success(`Ingested review #${created.id}`);
      onOpenChange(false);
    } catch (error) {
      handleApiError(error, "Failed to ingest review");
    }
  });

  const bulkRowPayloads = useMemo((): ExternalReviewCreate[] | null => {
    const reviews: ExternalReviewCreate[] = [];
    for (const row of bulkRows) {
      if (!row.url.trim()) continue;
      reviews.push({
        platform: row.platform,
        url: row.url.trim(),
        text: optionalString(row.text),
        author_handle: optionalString(row.author_handle),
      });
    }
    return reviews.length > 0 ? reviews : null;
  }, [bulkRows]);

  const handleBulkRowsSubmit = async () => {
    if (!sourceId) {
      toast.error("Select a source");
      return;
    }
    if (!bulkRowPayloads) {
      toast.error("Add at least one review with a URL");
      return;
    }
    try {
      const result = await ingestBulk.mutateAsync({
        sourceId,
        reviews: bulkRowPayloads,
      });
      toast.success(
        `Bulk ingest: ${result.inserted} inserted, ${result.skipped} skipped`,
      );
      if (result.skipped_urls.length > 0) {
        toast.message(`Skipped URLs: ${result.skipped_urls.join(", ")}`);
      }
      onOpenChange(false);
    } catch (error) {
      handleApiError(error, "Failed to bulk ingest reviews");
    }
  };

  const handleJsonSubmit = async () => {
    if (!sourceId) {
      toast.error("Select a source");
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonText);
    } catch {
      toast.error("Invalid JSON");
      return;
    }
    if (!Array.isArray(parsed) || parsed.length === 0) {
      toast.error("JSON must be a non-empty array of reviews");
      return;
    }
    const reviews = parsed as ExternalReviewCreate[];
    for (const [i, review] of reviews.entries()) {
      if (!review || typeof review !== "object") {
        toast.error(`Item ${i} is not an object`);
        return;
      }
      if (!review.platform || !review.url) {
        toast.error(`Item ${i} needs platform and url`);
        return;
      }
    }
    try {
      const result = await ingestBulk.mutateAsync({ sourceId, reviews });
      toast.success(
        `Bulk ingest: ${result.inserted} inserted, ${result.skipped} skipped`,
      );
      if (result.skipped_urls.length > 0) {
        toast.message(`Skipped URLs: ${result.skipped_urls.join(", ")}`);
      }
      onOpenChange(false);
    } catch (error) {
      handleApiError(error, "Failed to bulk ingest reviews");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add external review</DialogTitle>
          <DialogDescription>
            Ingest one or more external reviews for a source. Leave sentiment
            unset so they appear in the queue.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label>Source</Label>
          <SourceCombobox
            sources={sources}
            isLoading={sourcesLoading ?? false}
            value={sourceId}
            onValueChange={setSourceId}
            placeholder="Select source"
            className="w-full"
          />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="single">Single</TabsTrigger>
            <TabsTrigger value="rows">Multi-row</TabsTrigger>
            <TabsTrigger value="json">JSON</TabsTrigger>
          </TabsList>

          <TabsContent value="single" className="space-y-4 mt-4">
            <form
              id="ingest-single-form"
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                void onSingleSubmit();
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Platform</Label>
                  <Select
                    value={platform}
                    onValueChange={(value) =>
                      setValue("platform", value as ExternalReviewPlatform)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXTERNAL_REVIEW_PLATFORMS.map((p) => (
                        <SelectItem key={p} value={p}>
                          {PLATFORM_LABELS[p]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ingest-url">URL</Label>
                  <Input
                    id="ingest-url"
                    {...register("url", { required: true })}
                    placeholder="https://…"
                  />
                  {errors.url ? (
                    <p className="text-xs text-destructive">URL is required</p>
                  ) : null}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ingest-text">Text</Label>
                <Textarea
                  id="ingest-text"
                  rows={4}
                  {...register("text")}
                  placeholder="Review body (optional)"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="ingest-author">Author handle</Label>
                  <Input id="ingest-author" {...register("author_handle")} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ingest-external-id">External ID</Label>
                  <Input id="ingest-external-id" {...register("external_id")} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ingest-rating">Native rating</Label>
                  <Input
                    id="ingest-rating"
                    type="number"
                    step="any"
                    {...register("native_rating")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ingest-scale">Rating scale</Label>
                  <Input
                    id="ingest-scale"
                    type="number"
                    min={1}
                    {...register("native_rating_scale")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ingest-posted-at">Posted at</Label>
                  <Input
                    id="ingest-posted-at"
                    type="datetime-local"
                    {...register("posted_at")}
                  />
                </div>
                <div className="flex items-end gap-2 pb-2">
                  <Checkbox
                    id="ingest-vendor"
                    checked={isVendorEngagement}
                    onCheckedChange={(checked) => {
                      if (checked === "indeterminate") return;
                      setValue("is_vendor_engagement", Boolean(checked));
                    }}
                  />
                  <Label htmlFor="ingest-vendor" className="font-normal">
                    Vendor engagement
                  </Label>
                </div>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="rows" className="space-y-4 mt-4">
            <div className="space-y-3">
              {bulkRows.map((row, index) => (
                <div key={row.key} className="rounded-md border p-3 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">Row {index + 1}</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={bulkRows.length === 1}
                      onClick={() =>
                        setBulkRows((prev) =>
                          prev.filter((r) => r.key !== row.key),
                        )
                      }
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Platform</Label>
                      <Select
                        value={row.platform}
                        onValueChange={(value) =>
                          setBulkRows((prev) =>
                            prev.map((r) =>
                              r.key === row.key
                                ? {
                                    ...r,
                                    platform: value as ExternalReviewPlatform,
                                  }
                                : r,
                            ),
                          )
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {EXTERNAL_REVIEW_PLATFORMS.map((p) => (
                            <SelectItem key={p} value={p}>
                              {PLATFORM_LABELS[p]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>URL</Label>
                      <Input
                        value={row.url}
                        onChange={(e) =>
                          setBulkRows((prev) =>
                            prev.map((r) =>
                              r.key === row.key
                                ? { ...r, url: e.target.value }
                                : r,
                            ),
                          )
                        }
                        placeholder="https://…"
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label>Text</Label>
                      <Input
                        value={row.text}
                        onChange={(e) =>
                          setBulkRows((prev) =>
                            prev.map((r) =>
                              r.key === row.key
                                ? { ...r, text: e.target.value }
                                : r,
                            ),
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Author handle</Label>
                      <Input
                        value={row.author_handle}
                        onChange={(e) =>
                          setBulkRows((prev) =>
                            prev.map((r) =>
                              r.key === row.key
                                ? { ...r, author_handle: e.target.value }
                                : r,
                            ),
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setBulkRows((prev) => [...prev, emptyBulkRow()])}
            >
              <Plus className="w-4 h-4" />
              Add row
            </Button>
          </TabsContent>

          <TabsContent value="json" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="ingest-json">JSON array of reviews</Label>
              <Textarea
                id="ingest-json"
                rows={12}
                className="font-mono text-xs"
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                placeholder={`[\n  {\n    "platform": "reddit",\n    "url": "https://…",\n    "text": "…"\n  }\n]`}
              />
              <p className="text-xs text-muted-foreground">
                Each object needs at least <code>platform</code> and{" "}
                <code>url</code>. Sentiment fields should be omitted.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          {tab === "single" ? (
            <Button
              type="submit"
              form="ingest-single-form"
              disabled={sourceMissing || pending}
            >
              {ingestOne.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Ingesting…
                </>
              ) : (
                "Ingest review"
              )}
            </Button>
          ) : tab === "rows" ? (
            <Button
              type="button"
              disabled={sourceMissing || pending}
              onClick={() => void handleBulkRowsSubmit()}
            >
              {ingestBulk.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Ingesting…
                </>
              ) : (
                "Ingest rows"
              )}
            </Button>
          ) : (
            <Button
              type="button"
              disabled={sourceMissing || pending}
              onClick={() => void handleJsonSubmit()}
            >
              {ingestBulk.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Ingesting…
                </>
              ) : (
                "Ingest JSON"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
